import React from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Sync as SyncIcon,
  History as HistoryIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import PageLayout from '../common/PageLayout';
import { useAuth } from '../../contexts/AuthContext';

const SECTIONS = [
  { path: '/settings/sync', label: 'Data & sync', icon: <SyncIcon />, description: 'Refresh data from WooCommerce' },
  { path: '/settings/system-log', label: 'System Log', icon: <HistoryIcon />, description: 'Audit of key system actions', adminOnly: true },
];

export default function SettingsLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const path = location.pathname || '';
  const { staff } = useAuth();
  const perms = Array.isArray(staff?.permissions) ? staff.permissions : [];
  const isAdmin = perms.includes('admin:full') || String(staff?.role || '').toLowerCase() === 'admin';
  const sections = SECTIONS.filter((s) => !s.adminOnly || isAdmin);

  return (
    <PageLayout contentMaxWidth={false} showHeaderImage={false} showOrgHeader>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Settings
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isSmall ? 'column' : 'row',
          gap: 2,
          minHeight: 480,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: isSmall ? '100%' : 280,
            flexShrink: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ py: 1.5, px: 1 }}>
            <Typography variant="overline" color="text.secondary" sx={{ px: 2, display: 'block', mb: 0.5 }}>
              Sections
            </Typography>
            <List disablePadding>
              {sections.map((section) => {
                const active = path === section.path || (section.path !== '/settings/general' && path.startsWith(section.path));
                return (
                  <ListItemButton
                    key={section.path}
                    selected={active}
                    onClick={() => navigate(section.path)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: active ? 'inherit' : 'action.active' }}>
                      {section.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={section.label}
                      secondary={isSmall ? null : section.description}
                      primaryTypographyProps={{ fontWeight: 500 }}
                      secondaryTypographyProps={{ sx: { color: active ? 'inherit' : undefined } }}
                    />
                    <ChevronRightIcon sx={{ opacity: active ? 1 : 0.5 }} />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minWidth: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 3,
          }}
        >
          <Outlet />
        </Paper>
      </Box>
    </PageLayout>
  );
}
